import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LogIn, Loader2, XCircle, MailCheck, ShieldCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

export default function AdminLogin() {
    // 1. Aşama Stateleri
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    
    // 2. Aşama Stateleri (2FA)
    const [step, setStep] = useState(1); // 1: Login, 2: Verification
    const [verificationCode, setVerificationCode] = useState('');
    const [tempToken, setTempToken] = useState(''); // Kodu doğrulamak için geçici ID
    
    // Genel Stateler
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    // ADIM 1: Şifre ile Giriş İsteği
    const handleLoginSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const response = await axios.post(`${API_URL}/admin/login-request`, {
                username: email,
                password: password,
            });

            // Başarılıysa backend bize geçici bir referans id (veya sadece 200 OK) döner
            // Mail gönderildi, 2. aşamaya geçiyoruz.
            if(response.data.temp_id) {
                setTempToken(response.data.temp_id);
            }
            setStep(2); 
            
        } catch (err) {
            if (err.response) {
                setError(err.response.data.detail || "Giriş başarısız oldu. Lütfen bilgileri kontrol edin.");
            } else {
                setError("Sunucuya bağlanırken bir sorun oluştu.");
            }
        } finally {
            setLoading(false);
        }
    };

    // ADIM 2: Kod ile Doğrulama İsteği
    const handleVerificationSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const response = await axios.post(`${API_URL}/admin/verify-code`, {
                username: email,
                code: verificationCode,
                temp_id: tempToken // Backend güvenliği için gerekebilir
            });

            // Asıl token'ı BURA'da alıyoruz
            const { access_token, token_type } = response.data;

            localStorage.setItem('token', access_token);
            localStorage.setItem('tokenType', token_type);
            localStorage.setItem('userRole', "admin");

            navigate('/admin/dashboard'); 
        } catch (err) {
            if (err.response) {
                setError(err.response.data.detail || "Geçersiz veya süresi dolmuş doğrulama kodu.");
            } else {
                setError("Sunucuya bağlanırken bir sorun oluştu.");
            }
            // Çok fazla yanlış girilirse tekrar başa dönülebilir
            // if(err.response?.status === 403) setStep(1); 
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-900 font-sans p-4">
            <div className="bg-gray-800 p-8 sm:p-10 rounded-2xl shadow-2xl border border-gray-700 w-full max-w-md animate-in fade-in zoom-in duration-300">
                
                {/* Dinamik Başlık */}
                <div className="flex flex-col items-center mb-8 text-center">
                    {step === 1 ? (
                        <ShieldCheck className="h-16 w-16 text-blue-400 mb-4" />
                    ) : (
                        <MailCheck className="h-16 w-16 text-emerald-400 mb-4" />
                    )}
                    <h2 className="text-2xl sm:text-3xl font-extrabold text-white">
                        {step === 1 ? "Yönetici Paneli" : "Güvenlik Doğrulaması"}
                    </h2>
                    {step === 2 && (
                        <p className="text-sm text-gray-400 mt-2">
                            <span className="text-white font-medium">admin@mekanname.com</span> adresine gönderilen 6 haneli kodu girin.
                        </p>
                    )}
                </div>

                {error && (
                    <div className="flex items-center gap-3 p-4 mb-6 rounded-lg bg-red-900/30 text-red-400 border border-red-700/50">
                        <XCircle className="h-5 w-5 flex-shrink-0" />
                        <span className="font-medium text-sm leading-snug">{error}</span>
                    </div>
                )}

                {/* ADIM 1 FORMU */}
                {step === 1 && (
                    <form onSubmit={handleLoginSubmit} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">E-posta</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="block w-full px-4 py-3 bg-gray-700 text-gray-200 rounded-xl shadow-inner border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                                placeholder="Kullanıcı Adı"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Şifre</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="block w-full px-4 py-3 bg-gray-700 text-gray-200 rounded-xl shadow-inner border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                                placeholder="••••••••"
                            />
                        </div>
                        
                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold text-white transition-all duration-200
                            ${loading ? "bg-blue-600/50 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-500 active:scale-95 shadow-lg shadow-blue-900/50"}`}
                        >
                            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <LogIn className="h-5 w-5" />}
                            {loading ? "Kontrol Ediliyor..." : "Devam Et"}
                        </button>
                    </form>
                )}

                {/* ADIM 2 FORMU */}
                {step === 2 && (
                    <form onSubmit={handleVerificationSubmit} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2 text-center">Doğrulama Kodu</label>
                            <input
                                type="text"
                                maxLength="6"
                                value={verificationCode}
                                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))} // Sadece rakam
                                required
                                autoFocus
                                className="block w-full px-4 py-4 text-center text-3xl tracking-[1em] font-mono bg-gray-700 text-white rounded-xl shadow-inner border border-gray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
                                placeholder="------"
                            />
                        </div>
                        
                        <button
                            type="submit"
                            disabled={loading || verificationCode.length !== 6}
                            className={`w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold text-white transition-all duration-200
                            ${loading || verificationCode.length !== 6 ? "bg-emerald-600/50 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-500 active:scale-95 shadow-lg shadow-emerald-900/50"}`}
                        >
                            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShieldCheck className="h-5 w-5" />}
                            {loading ? "Doğrulanıyor..." : "Giriş Yap"}
                        </button>
                        
                        <button 
                            type="button" 
                            onClick={() => { setStep(1); setVerificationCode(''); setError(''); }}
                            className="w-full text-sm text-gray-400 hover:text-white transition-colors"
                        >
                            Giriş ekranına dön
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}